import { useEffect, useState } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';

import { useAuth0User } from '@hooks/useAuth0User';
import { useAccessToken } from '@hooks/useAccessToken';
import { confirmCheckout } from '@api-service/checkout';
import { datadogRumService } from '@lib/datadogRum';
import { EventCategoryDict, MembershipLTVDict } from '@lib/gtag';
import { MEMBERSHIPS } from '@lib/constants';
import { TwitterEvents } from '@lib/twitter-pixel';
import { clearStoredReferralId } from '@lib/rewardful';
import { ConsentManager } from '@lib/consent';
import {
  buildPurchaseEvent,
  buildStartTrialEvent,
  mapConsentToSignals,
  PURCHASE_CURRENCY,
  pushPurchase,
  pushStartTrial,
  readClickIds,
  resolvePlanTier,
  resolvePurchaseEventId,
  resolvePurchaseValue
} from '@lib/analytics/dataLayer';
import {
  AnalyticsEvents,
  captureAnalyticsEvent,
  toAnalyticsMembershipTier
} from '@lib/analytics/events';

type PurchaseConfirmationAnalyticsParams = {
  planId?: string;
  sessionId?: string;
};

type ConfirmResult = {
  hashedEmail?: string | null;
  purchaseEventId?: string | null;
  isTrial?: boolean;
  // FFH-1847: actual charged amount + currency for the localised purchase push.
  amount?: number | null;
  currency?: string | null;
  // Confirm failed or couldn't run: trial-vs-upfront is unknowable, so the
  // push effect must not fire either event (the browser must never guess a
  // `purchase` for what may be a trial — the server CAPI still captures the
  // conversion). No idempotency marker is written, so a reload retries.
  confirmFailed?: boolean;
  isSettled: boolean;
};

/**
 * The post-checkout purchase analytics suite, extracted verbatim from
 * /thank-you so the funnel welcome page fires the identical rail:
 * `POST /billing/confirm` for the server-authoritative event id, the GTM
 * page_view, and the idempotent `purchase` dataLayer push that the GTM
 * Purchase pixel tags read from (FFH-359 single browser rail, deduped
 * against the Stripe-webhook CAPI on the shared event_id).
 */
export const usePurchaseConfirmationAnalytics = ({
  planId,
  sessionId
}: PurchaseConfirmationAnalyticsParams) => {
  const { user, isLoading } = useAuth0User();
  const { accessToken, accessTokenIsFetching } = useAccessToken();
  // Purchase confirmation requires the user's JWT, so it runs client-side.
  // `isSettled` lets the pixel effects wait for the hashed email without
  // blocking forever when confirmation is unavailable.
  const [confirmResult, setConfirmResult] = useState<ConfirmResult>({
    isSettled: false
  });
  // TODO(FFH-1298): wire twitterHasLoaded to a readiness flag set by the Twitter pixel script onLoad in _app.tsx.
  const twitterHasLoaded = false;

  useEffect(() => {
    clearStoredReferralId();
  }, []);

  useEffect(() => {
    if (confirmResult.isSettled) {
      return;
    }

    // Auth0 finished loading with no user: useAccessToken never starts its
    // token fetch in that case, so accessTokenIsFetching stays true forever.
    // Settle as failed explicitly instead of waiting on it.
    if (!isLoading && !user) {
      setConfirmResult({ confirmFailed: true, isSettled: true });
      return;
    }

    if (accessTokenIsFetching) {
      return;
    }

    if (!sessionId || !accessToken) {
      // Without a token the confirm call can't run, so trial-vs-upfront is
      // unknowable — settle as failed rather than guessing.
      setConfirmResult({ confirmFailed: true, isSettled: true });
      return;
    }

    const confirmPurchase = async () => {
      try {
        const {
          ok,
          failureStatus,
          hashedEmail,
          purchaseEventId,
          isTrial,
          amount,
          currency
        } = await confirmCheckout({
          accessToken,
          sessionId
        });
        if (!ok) {
          // A failed confirm means no browser purchase/start_trial push fires
          // for this conversion (the server CAPI still records it). RUM is the
          // only place this surfaces — nothing else observes the failure.
          datadogRumService.addError(
            new Error('Purchase confirmation failed'),
            {
              context: 'purchase-confirmation',
              stage: 'confirm_checkout',
              status: failureStatus,
              sessionId,
              planId
            }
          );
        }
        setConfirmResult({
          hashedEmail,
          purchaseEventId,
          isTrial,
          amount,
          currency,
          confirmFailed: !ok,
          isSettled: true
        });
      } catch (error) {
        // confirmCheckout threw (client init). Settle as failed so the
        // hashed-email consumers are never blocked forever, but push nothing.
        datadogRumService.addError(
          error instanceof Error
            ? error
            : new Error('Purchase confirmation threw'),
          {
            context: 'purchase-confirmation',
            stage: 'confirm_checkout',
            status: null,
            sessionId,
            planId
          }
        );
        setConfirmResult({ confirmFailed: true, isSettled: true });
      }
    };

    confirmPurchase();
  }, [
    sessionId,
    accessToken,
    accessTokenIsFetching,
    confirmResult.isSettled,
    isLoading,
    user
  ]);

  useEffect(() => {
    // The dicts are keyed by numeric membership id; the query param arrives
    // as a string, so normalise before the lookup (unknown ids stay
    // undefined, matching the original page behaviour).
    const membershipId = planId ? Number(planId) : undefined;
    sendGTMEvent({
      event: 'page_view',
      plan_id: membershipId ? EventCategoryDict[membershipId] : undefined,
      plan_value: membershipId ? MembershipLTVDict[membershipId] : undefined,
      user_logged_in: false
    });
  }, [planId]);

  useEffect(() => {
    if (
      sessionId &&
      twitterHasLoaded &&
      planId &&
      Number(planId) !== MEMBERSHIPS.FREE_ID
    ) {
      window.twq('event', TwitterEvents.purchasePaidPlan);
    }
  }, [twitterHasLoaded, sessionId]);

  // FFH-1170: emit the GTM-consumed `purchase` dataLayer push that the Meta /
  // Google Ads / TikTok / Snap pixel tags read from. Fires once auth has
  // resolved (so the plaintext email + Auth0 sub are available). Not gated on
  // CookieYes — Consent Mode v2 reads the carried consent snapshot at tag
  // firing time (cross-cutting rule 5). Idempotent per session so a re-render
  // cannot double-push.
  useEffect(() => {
    // Wait for confirmResult so the push carries the server-authoritative
    // purchase_event_id (FFH-359) — guaranteeing the GTM Purchase pixels dedup
    // against the Stripe-webhook CAPI on the same id. Falls back to the
    // deterministic id only if confirmation was unavailable.
    if (!sessionId || isLoading || !confirmResult.isSettled) return;

    // Confirm failed → trial-vs-upfront unknowable → push nothing (Matt,
    // option (a): the browser must never count a purchase at trial start).
    // The Stripe-webhook CAPI still records the conversion server-side, and
    // with no idempotency marker written a page reload retries the confirm.
    if (confirmResult.confirmFailed) return;

    // Analytics must never break the confirmation page. window.localStorage
    // can throw in private mode, when storage is disabled, or inside some
    // in-app webviews, so the whole idempotency + push flow fails soft.
    // hasPushed separates a lost push from a failed idempotency write after
    // a successful push — only the former is a lost conversion.
    let hasPushed = false;
    try {
      const idempotencyKey = `ffh_purchase_dl_${sessionId}`;
      if (window.localStorage.getItem(idempotencyKey)) return;

      const input = {
        eventId:
          confirmResult.purchaseEventId ?? resolvePurchaseEventId(sessionId),
        stripeSessionId: sessionId,
        planId,
        email: user?.email,
        externalId: user?.sub,
        click: readClickIds(),
        sourceUrl: window.location.href,
        referrer: document.referrer || undefined,
        consent: mapConsentToSignals(new ConsentManager().getCurrentConsent())
      };

      // Trials push `start_trial` instead of `purchase`: the browser must not
      // count a purchase at trial start (Matt, option (a)) — the Purchase for
      // a trial fires server-side only, at the Stripe trialing→active flip.
      // FFH-1847: the actual charged amount/currency applies to the PURCHASE
      // event only. Start Trial keeps its annual estimate (it's a £0 charge at
      // trial start, and changing that optimisation signal is out of scope).
      // FFH-2372: PostHog inside the same branch, guards and idempotency key
      // as the dataLayer push, so trial_started and purchase_completed stay
      // mutually exclusive exactly the way start_trial and purchase are.
      const analyticsTier = toAnalyticsMembershipTier(resolvePlanTier(planId));
      if (confirmResult.isTrial) {
        pushStartTrial(buildStartTrialEvent(input));
        captureAnalyticsEvent(AnalyticsEvents.trialStarted, {
          tier: analyticsTier
        });
      } else {
        pushPurchase(
          buildPurchaseEvent({
            ...input,
            amount: confirmResult.amount,
            currency: confirmResult.currency
          })
        );
        captureAnalyticsEvent(AnalyticsEvents.purchaseCompleted, {
          tier: analyticsTier,
          billing_period: 'annual',
          // Mirror the dataLayer rail: charged amount/currency from the
          // confirm when available (FFH-1847), plan-dict estimate otherwise.
          value: confirmResult.amount ?? resolvePurchaseValue(planId),
          currency: confirmResult.currency ?? PURCHASE_CURRENCY
        });
      }
      hasPushed = true;
      window.localStorage.setItem(idempotencyKey, 'true');
    } catch (error) {
      // Never break a confirmation page for an analytics push, but do report
      // it. datalayer_push means the purchase/start_trial push was lost and
      // every ad platform undercounts this conversion; idempotency_write
      // means the push landed but the marker write failed, so a reload could
      // double-push.
      datadogRumService.addError(
        error instanceof Error ? error : new Error('Purchase push failed'),
        {
          context: 'purchase-confirmation',
          stage: hasPushed ? 'idempotency_write' : 'datalayer_push',
          sessionId,
          planId
        }
      );
    }
  }, [sessionId, isLoading, planId, user, confirmResult]);

  return { isConfirmSettled: confirmResult.isSettled };
};
